import { lerDaApi as ler } from './giro';

/**
 * BLOCO 12 — o front de /api/paineis (telas 05, 11 e 12). Espelha `@inovapay/database/paineis`.
 *
 * Indicador indisponível traz o MOTIVO: a tela mostra "indisponível · motivo", nunca zero.
 * Toda variação vem com a base ("média das últimas 4 terças", "período anterior").
 */

export interface Periodo {
  readonly de: string;
  readonly ate: string;
}
export type Sentido = 'mais_e_bom' | 'mais_e_ruim' | 'neutro';
export type Indicador =
  | {
      readonly disponivel: true;
      readonly valor: number;
      readonly detalhe: string | null;
      readonly variacao: { readonly pct: number; readonly base: string } | null;
      readonly sentido: Sentido;
      readonly serie: readonly number[] | null;
      readonly fonte: string;
      readonly link: string;
    }
  | { readonly disponivel: false; readonly porque: string; readonly link: string };

export interface FrescorDaFonte {
  readonly fonte: 'vendas' | 'giro' | 'relatorio_hos' | 'estoque';
  readonly rotulo: string;
  readonly ate: string | null;
  readonly estado: 'ok' | 'antigo' | 'indisponivel';
  readonly detalhe: string;
}
export interface SerieHoraria {
  readonly metrica: 'faturamento' | 'itens';
  readonly fonte: 'relatorio_hos' | 'inovapay' | null;
  readonly base: string | null;
  readonly horas: readonly {
    readonly hora: number;
    readonly atual: number;
    readonly base: number | null;
    readonly futura: boolean;
  }[];
  readonly porque: string | null;
}
export interface ItemDeAtencao {
  readonly tipo: string;
  readonly severidade: 'critica' | 'atencao' | 'info';
  readonly titulo: string;
  readonly detalhe: string;
  readonly acao: string;
  readonly link: string;
}
export interface ModuloDaOperacao {
  readonly modulo: 'giro' | 'vendas' | 'premiacao' | 'pre_vencidos' | 'paradao';
  readonly titulo: string;
  readonly subtitulo: string;
  readonly progresso: number | null;
  readonly resumo: string;
  readonly selos: readonly {
    readonly texto: string;
    readonly tom: 'ok' | 'atencao' | 'critico' | 'neutro';
  }[];
  readonly acao: string;
  readonly link: string;
}
export interface Destaques {
  readonly de: string;
  readonly ate: string;
  readonly metaSemanal: number;
  readonly linhas: readonly {
    readonly posicao: number;
    readonly accountId: string;
    readonly nome: string;
    readonly itens: number;
  }[];
}
export interface PainelDoDia {
  readonly dia: string;
  readonly atualizadoEm: string;
  readonly frescor: readonly FrescorDaFonte[];
  readonly kpis: {
    readonly vendas: Indicador;
    readonly itens: Indicador;
    readonly cupons: Indicador;
    readonly ticket: Indicador;
    readonly premiacao: Indicador;
    readonly meta: Indicador;
    readonly giro: Indicador;
    readonly divergencias: Indicador;
  };
  readonly porHora: SerieHoraria;
  readonly atencao: readonly ItemDeAtencao[];
  readonly operacao: readonly ModuloDaOperacao[];
  readonly atividade: readonly {
    readonly em: string;
    readonly quem: string | null;
    readonly texto: string;
    /** A mesma ação repetida em sequência vira uma linha: quantas vezes e desde quando. */
    readonly vezes: number;
    readonly desde: string;
  }[];
  readonly destaques: Destaques;
}
export interface DashboardGerencial {
  readonly periodo: Periodo;
  readonly base: string | null;
  readonly atualizadoEm: string;
  readonly frescor: readonly FrescorDaFonte[];
  readonly indicadores: {
    readonly itens: Indicador;
    readonly faturamento: Indicador;
    readonly ticket: Indicador;
    readonly premiacao: Indicador;
    readonly giro: Indicador;
    readonly equipe: Indicador;
    readonly divergencias: Indicador;
  };
  readonly porHora: SerieHoraria;
  readonly porDia: readonly {
    readonly dia: string;
    readonly faturamento: number;
    readonly itens: number;
    readonly base: number | null;
  }[];
  readonly atencao: readonly ItemDeAtencao[];
  readonly operacao: readonly ModuloDaOperacao[];
  readonly top: Destaques;
}
export type MetricaDoGrafico = 'faturamento' | 'itens' | 'cupons' | 'ticket';
export type DimensaoDoGrafico = 'hora' | 'dia' | 'dia_semana' | 'balconista' | 'tipo';
export interface Grafico {
  readonly metrica: MetricaDoGrafico;
  readonly por: DimensaoDoGrafico;
  readonly periodo: Periodo;
  readonly comparar: Periodo | null;
  readonly fonte: 'relatorio_hos' | 'inovapay' | null;
  readonly porque: string | null;
  readonly pontos: readonly {
    readonly chave: string;
    readonly rotulo: string;
    readonly atual: number;
    readonly anterior: number | null;
  }[];
  readonly semana: readonly {
    readonly dia: number;
    readonly rotulo: string;
    readonly media: number | null;
  }[];
  readonly calor: readonly {
    readonly dia: number;
    readonly hora: number;
    readonly valor: number;
  }[];
  readonly composicao: readonly {
    readonly tipo: string;
    readonly itens: number;
    readonly pct: number;
  }[];
}
export interface Insight {
  readonly id: string;
  readonly grupo: 'atencao' | 'destaque' | 'tendencia';
  readonly icone: 'baixa' | 'alta' | 'relogio' | 'trofeu';
  readonly titulo: string;
  readonly fato: string;
  readonly atual: { readonly rotulo: string; readonly valor: string };
  readonly base: { readonly rotulo: string; readonly valor: string };
  readonly calculo: string;
  readonly serie: readonly { readonly rotulo: string; readonly valor: number }[];
  readonly acao: { readonly rotulo: string; readonly link: string };
  readonly exige: string;
}
export interface Insights {
  readonly periodo: Periodo;
  readonly calculadoEm: string;
  readonly itens: readonly Insight[];
}

export const lerPainelDoDia = (dia?: string) =>
  ler<PainelDoDia>(`/api/paineis/dia${dia ? `?dia=${dia}` : ''}`);
export const lerDashboard = (p: Periodo) =>
  ler<DashboardGerencial>(`/api/paineis/dashboard?de=${p.de}&ate=${p.ate}`);
export const lerGrafico = (q: {
  metrica: MetricaDoGrafico;
  por: DimensaoDoGrafico;
  periodo: Periodo;
  comparar: boolean;
}) =>
  ler<Grafico>(
    `/api/paineis/graficos?metrica=${q.metrica}&por=${q.por}&de=${q.periodo.de}&ate=${q.periodo.ate}&comparar=${q.comparar ? 'sim' : 'nao'}`,
  );
export const lerInsights = (dias = 7) =>
  ler<Insights>(`/api/paineis/insights?dias=${String(dias)}`);

/* ------------------------------------------------------------------ *
 * Formatos
 * ------------------------------------------------------------------ */

export const reais = (v: number, casas = 2) =>
  `R$ ${v.toLocaleString('pt-BR', { minimumFractionDigits: casas, maximumFractionDigits: casas })}`;
export const inteiro = (v: number) => v.toLocaleString('pt-BR', { maximumFractionDigits: 0 });
export const pctTexto = (v: number) => `${Math.abs(v).toFixed(1).replace('.', ',')}%`;
/** Variação boa ou ruim pela SEMÂNTICA do indicador (divergência em alta é ruim). */
export function tomDaVariacao(i: Indicador): 'bom' | 'ruim' | 'neutro' {
  if (!i.disponivel || !i.variacao || i.variacao.pct === 0 || i.sentido === 'neutro')
    return 'neutro';
  const subiu = i.variacao.pct > 0;
  return (i.sentido === 'mais_e_bom') === subiu ? 'bom' : 'ruim';
}
export function hojeBrasilia(): string {
  return new Date().toLocaleDateString('sv-SE', { timeZone: 'America/Sao_Paulo' });
}
export const maisDias = (d: string, n: number) =>
  new Date(Date.parse(`${d}T12:00:00Z`) + n * 86_400_000).toISOString().slice(0, 10);
export type AtalhoDePeriodo = 'hoje' | 'ontem' | '7d' | '30d' | 'mes';
export function periodoDo(a: AtalhoDePeriodo, hoje = hojeBrasilia()): Periodo {
  if (a === 'hoje') return { de: hoje, ate: hoje };
  if (a === 'ontem') return { de: maisDias(hoje, -1), ate: maisDias(hoje, -1) };
  if (a === '7d') return { de: maisDias(hoje, -6), ate: hoje };
  if (a === '30d') return { de: maisDias(hoje, -29), ate: hoje };
  return { de: `${hoje.slice(0, 8)}01`, ate: hoje };
}
export const horaCurta = (iso: string) =>
  new Date(iso).toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'America/Sao_Paulo',
  });
export const diaLongo = (d: string) =>
  new Date(`${d}T12:00:00Z`).toLocaleDateString('pt-BR', {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });

/* ------------------------------------------------------------------ *
 * Galeria de gráficos (seção Gráficos, Dashboard e ficha do balconista)
 * ------------------------------------------------------------------ */
export interface DiaDaGaleria {
  readonly dia: string;
  readonly faturamento: number;
  readonly cupons: number;
  readonly itens: number;
  readonly ticket: number | null;
  readonly itensPorCupom: number | null;
}
export interface GaleriaDeGraficos {
  readonly periodo: Periodo;
  readonly anterior: Periodo;
  readonly pessoa: { readonly id: string; readonly nome: string; readonly codigo: string } | null;
  readonly diario: readonly DiaDaGaleria[];
  readonly diarioAnterior: readonly DiaDaGaleria[];
  readonly porHora: readonly { hora: number; faturamento: number; cupons: number }[];
  readonly mapaDeCalor: readonly {
    dow: number;
    hora: number;
    cupons: number;
    faturamento: number;
  }[];
  readonly porBalconista: readonly {
    readonly nome: string;
    readonly codigo: string | null;
    readonly faturamento: number;
    readonly cupons: number;
    readonly itens: number;
    readonly ticket: number;
    readonly itensPorCupom: number;
    readonly produtosDiferentesPorCupom: number;
  }[];
  readonly programas: readonly {
    dia: string;
    programa: 'vitamina' | 'pre_vencido' | 'paradao';
    itens: number;
    premio: number;
  }[];
  readonly topProdutos: readonly {
    nome: string;
    codigo: string | null;
    faturamento: number;
    quantidade: number;
  }[];
  readonly velas: readonly {
    dia: string;
    abertura: number;
    fechamento: number;
    minimo: number;
    maximo: number;
  }[];
  readonly meta: {
    readonly semana: Periodo;
    readonly itensPorPessoa: number;
    readonly pessoas: readonly { nome: string; itens: number }[];
  };
  readonly funil: { lancados: number; ativos: number; emSemanaPaga: number };
  readonly venn: readonly { programas: readonly string[]; pessoas: number }[];
}
export const lerGaleria = (p: Periodo, pessoa?: string | null) =>
  ler<GaleriaDeGraficos>(
    `/api/paineis/galeria?de=${p.de}&ate=${p.ate}${pessoa ? `&pessoa=${pessoa}` : ''}`,
  );

/* ------------------------------------------------------------------ *
 * Insights novos (admin: onde agir · balconista: como estou indo)
 * ------------------------------------------------------------------ */
export type TomDoInsight = 'alerta' | 'oportunidade' | 'positivo' | 'neutro';
export type IconeDoInsight =
  | 'alta'
  | 'baixa'
  | 'dinheiro'
  | 'meta'
  | 'relogio'
  | 'trofeu'
  | 'caixa'
  | 'lupa'
  | 'escudo'
  | 'pessoas'
  | 'raio';
export interface CartaoDeInsight {
  readonly id: string;
  readonly tom: TomDoInsight;
  readonly icone: IconeDoInsight;
  readonly titulo: string;
  readonly numero: string | null;
  readonly linhas: readonly string[];
  readonly serie?: readonly { readonly rotulo: string; readonly valor: number }[];
  readonly calculo: string;
  readonly acao: { readonly rotulo: string; readonly link: string } | null;
}
export interface InsightsDoAdmin {
  readonly calculadoEm: string;
  readonly hoje: string;
  readonly resumo: readonly string[];
  readonly grupos: readonly {
    readonly id: string;
    readonly titulo: string;
    readonly cartoes: readonly CartaoDeInsight[];
  }[];
}
export interface InsightsDoBalconista {
  readonly calculadoEm: string;
  readonly pessoa: { readonly nome: string; readonly codigo: string };
  readonly semana: Periodo;
  readonly cartoes: readonly CartaoDeInsight[];
}
export const lerInsightsDoAdmin = () => ler<InsightsDoAdmin>('/api/insights/admin');
export const lerMeusInsights = () => ler<InsightsDoBalconista>('/api/insights/meus');

/* ------------------------------------------------------------------ *
 * Painel do InovaPay — o "hoje" dos programas (sem número da loja)
 * ------------------------------------------------------------------ */
export interface HojeNoInovapay {
  readonly dia: string;
  readonly atencao: readonly ItemDeAtencao[];
  readonly operacao: readonly ModuloDaOperacao[];
}
export const lerHojeNoInovapay = () => ler<HojeNoInovapay>('/api/inovapay/hoje');

/* ------------------------------------------------------------------ *
 * Lucro do InovaPay (dado da LOJA — só o dono)
 * ------------------------------------------------------------------ */
export type ProgramaDoLucro = 'vitamina' | 'pre_vencido' | 'paradao';
export interface TotaisDoLucro {
  readonly itens: number;
  readonly vendido: number;
  readonly custo: number;
  readonly seVendesseNoBase: number | null;
  readonly ganhoExtra: number | null;
  readonly premiacao: number;
  readonly lucroLiquido: number;
  readonly lucroExtraDoApp: number | null;
  readonly parteDoAtendente: number | null;
  readonly parteDaFarmacia: number | null;
  readonly semCusto: number;
}
export interface LucroDoInovapay {
  readonly periodo: Periodo;
  readonly total: TotaisDoLucro;
  readonly porPrograma: Readonly<Record<ProgramaDoLucro, TotaisDoLucro>>;
  readonly porBalconista: readonly (TotaisDoLucro & {
    readonly accountId: string;
    readonly nome: string;
    readonly codigo: string;
    readonly porPrograma: Readonly<Record<ProgramaDoLucro, number>>;
  })[];
  readonly porDia: readonly {
    dia: string;
    vendido: number;
    lucroLiquido: number;
    premiacao: number;
  }[];
  readonly balconistas: readonly { accountId: string; nome: string }[];
}
export const lerLucroDoInovapay = (p: Periodo, pessoa?: string | null) =>
  ler<LucroDoInovapay>(
    `/api/lucro-inovapay?de=${p.de}&ate=${p.ate}${pessoa ? `&pessoa=${pessoa}` : ''}`,
  );
